import React, { useState } from "react";
import { Check, Pencil } from "lucide-react";
import { espaceApi, messageErreur, CLE_JETON } from "./api";
import { ChampNaissance } from "../components/ChampNaissance";

const Champ = ({ label, ...props }) => (
  <div>
    <label className="field-label">{label}</label>
    <input className="input-field" {...props} />
  </div>
);

/* Les coordonnées de la famille, modifiables par elle. Rien n'attend de validation :
   la correction s'applique et apparaît dans le journal de la console. C'est ce qui évite
   qu'une adresse reste fausse — et sans rue, NPA et localité, le bulletin QR est refusé. */
export const Coordonnees = ({ moi, onEnregistre }) => {
  const [ouvert, setOuvert] = useState(false);
  const [form, setForm] = useState({
    adresse: moi.adresse || "", npa: moi.npa || "",
    localite: moi.localite || "", telephone: moi.telephone || "",
  });
  const [etat, setEtat] = useState("");
  const [erreur, setErreur] = useState("");
  const incomplet = !form.adresse || !form.npa || !form.localite;

  const enregistrer = async (e) => {
    e.preventDefault();
    setErreur("");
    setEtat("envoi");
    try {
      const { data } = await espaceApi.patch("/profil", form);
      setEtat(data.message);
      await onEnregistre();
      setOuvert(false);
    } catch (err) {
      setErreur(messageErreur(err));
      setEtat("");
    }
  };

  const update = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  return (
    <div className="card-flat" data-testid="espace-coordonnees">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="eyebrow mb-1">Mes coordonnées</div>
          <div className="text-sm">
            {[moi.adresse, [moi.npa, moi.localite].filter(Boolean).join(" ")]
              .filter(Boolean).join(" · ") || "Adresse non renseignée"}
          </div>
          {moi.telephone && (
            <div className="text-xs text-[var(--muted)] mt-1">{moi.telephone}</div>
          )}
          <div className="text-xs text-[var(--muted)] mt-1">{moi.email}</div>
        </div>
        <button onClick={() => setOuvert(!ouvert)} className="btn-ghost text-sm shrink-0"
          data-testid="espace-modifier-coordonnees">
          <Pencil size={14} /> {ouvert ? "Fermer" : "Modifier"}
        </button>
      </div>

      {incomplet && !ouvert && (
        <p className="text-xs mt-3 rounded-2xl px-4 py-3" data-testid="espace-adresse-manquante"
          style={{ background: "#FFFBEB", color: "#B45309" }}>
          Il manque ton adresse complète (rue, code postal et localité) : sans elle, on ne
          peut pas te faire de facture avec le QR-code.
        </p>
      )}

      {ouvert && (
        <form onSubmit={enregistrer} className="mt-5 space-y-4"
          data-testid="espace-formulaire-coordonnees">
          <Champ label="Rue et numéro" data-testid="espace-champ-adresse"
            value={form.adresse} onChange={update("adresse")} />
          <div className="grid grid-cols-3 gap-3">
            <Champ label="NPA" data-testid="espace-champ-npa" inputMode="numeric"
              value={form.npa} onChange={update("npa")} />
            <div className="col-span-2">
              <Champ label="Localité" data-testid="espace-champ-localite"
                value={form.localite} onChange={update("localite")} />
            </div>
          </div>
          <Champ label="Téléphone" data-testid="espace-champ-telephone" type="tel"
            value={form.telephone} onChange={update("telephone")} />
          {erreur && (
            <div className="text-sm rounded-2xl px-4 py-3"
              style={{ background: "#FEF2F2", color: "#B91C1C" }}>{erreur}</div>
          )}
          <button type="submit" disabled={etat === "envoi"} className="btn-primary text-sm"
            data-testid="espace-enregistrer-coordonnees">
            {etat === "envoi" ? "Enregistrement…" : "Enregistrer"}
          </button>
        </form>
      )}

      {etat && etat !== "envoi" && !ouvert && (
        <p className="text-xs mt-3 flex items-center gap-1" style={{ color: "#15803D" }}
          data-testid="espace-coordonnees-ok">
          <Check size={12} /> {etat}
        </p>
      )}
    </div>
  );
};

/* La fiche d'un enfant : le numéro AVS (que l'école réclame sinon par e-mail), sa date de
   naissance et le droit à l'image. */
export const FicheEnfant = ({ enfant, onEnregistre }) => {
  const [form, setForm] = useState({
    avs: enfant.avs || "", date_naissance: enfant.date_naissance || "",
    droit_image: enfant.droit_image || "",
  });
  const [etat, setEtat] = useState("");
  const [erreur, setErreur] = useState("");

  const enregistrer = async (e) => {
    e.preventDefault();
    setErreur("");
    setEtat("envoi");
    try {
      const { data } = await espaceApi.patch(`/enfant/${encodeURIComponent(enfant.id)}`, form);
      setEtat(data.message);
      await onEnregistre();
    } catch (err) {
      setErreur(messageErreur(err));
      setEtat("");
    }
  };

  return (
    <form onSubmit={enregistrer} className="pt-1 space-y-4"
      data-testid={`espace-fiche-${enfant.id}`}>
      <Champ label="Numéro AVS" data-testid={`espace-avs-${enfant.id}`}
        placeholder="756.XXXX.XXXX.XX" value={form.avs}
        onChange={(e) => setForm({ ...form, avs: e.target.value })} />
      <div>
        <label className="field-label">Date de naissance</label>
        <ChampNaissance testid={`espace-naissance-${enfant.id}`} value={form.date_naissance}
          onChange={(v) => setForm((f) => ({ ...f, date_naissance: v }))} />
      </div>
      <div>
        <label className="field-label">Droit à l'image</label>
        <div className="flex gap-2">
          {["Oui", "Non"].map((choix) => (
            <button key={choix} type="button"
              onClick={() => setForm({ ...form, droit_image: choix })}
              className="px-4 py-2 text-xs rounded-full"
              data-testid={`espace-image-${choix.toLowerCase()}-${enfant.id}`}
              style={form.droit_image === choix
                ? { background: "var(--primary)", color: "#fff" }
                : { background: "var(--primary-tint)", color: "var(--text)" }}>
              {choix === "Oui" ? "J'autorise les photos" : "Je refuse les photos"}
            </button>
          ))}
        </div>
      </div>
      {erreur && (
        <div className="text-sm rounded-2xl px-4 py-3"
          style={{ background: "#FEF2F2", color: "#B91C1C" }}>{erreur}</div>
      )}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={etat === "envoi"} className="btn-primary text-sm"
          data-testid={`espace-enregistrer-${enfant.id}`}>
          {etat === "envoi" ? "Enregistrement…" : "Enregistrer"}
        </button>
        {etat && etat !== "envoi" && (
          <span className="text-xs flex items-center gap-1" style={{ color: "#15803D" }}>
            <Check size={12} /> {etat}
          </span>
        )}
      </div>
    </form>
  );
};

/* Mot de passe et adresse de connexion. Une famille arrivée par lien e-mail ou par Google
   peut se poser un mot de passe ici, sans repasser par une inscription. */
export const Securite = ({ email, onEmailChange }) => {
  const [ouvert, setOuvert] = useState(false);
  const [motDePasse, setMotDePasse] = useState("");
  const [nouvelEmail, setNouvelEmail] = useState("");
  const [message, setMessage] = useState("");
  const [erreur, setErreur] = useState("");

  const agir = async (chemin, corps) => {
    setErreur("");
    setMessage("");
    try {
      const { data } = await espaceApi.post(chemin, corps);
      if (data.session) localStorage.setItem(CLE_JETON, data.session);
      setMessage(data.message);
      setMotDePasse("");
      setNouvelEmail("");
      if (data.email && data.email !== email) await onEmailChange();
    } catch (err) {
      setErreur(messageErreur(err));
    }
  };

  return (
    <div className="card-flat" data-testid="espace-securite">
      <div className="flex items-center justify-between gap-4">
        <div className="eyebrow">Mot de passe et connexion</div>
        <button onClick={() => setOuvert(!ouvert)} className="btn-ghost text-sm shrink-0"
          data-testid="espace-ouvrir-securite">
          {ouvert ? "Fermer" : "Modifier"}
        </button>
      </div>

      {ouvert && (
        <div className="mt-5 space-y-6">
          <div className="space-y-3">
            <Champ label="Nouveau mot de passe (8 caractères minimum)" type="password"
              autoComplete="new-password" data-testid="espace-nouveau-mot-de-passe"
              value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} />
            <button disabled={motDePasse.length < 8} className="btn-primary text-sm"
              onClick={() => agir("/mot-de-passe", { mot_de_passe: motDePasse })}
              data-testid="espace-enregistrer-mot-de-passe">
              Enregistrer le mot de passe
            </button>
          </div>

          <div className="space-y-3 pt-5 border-t" style={{ borderColor: "var(--border)" }}>
            <Champ label="Nouvelle adresse e-mail" type="email"
              data-testid="espace-nouvel-email" placeholder={email}
              value={nouvelEmail} onChange={(e) => setNouvelEmail(e.target.value)} />
            <p className="text-[11px] text-[var(--muted)]">
              C'est aussi ton identifiant de connexion, et l'adresse où partent tes factures
              et attestations : les deux changent en même temps. On écrit d'abord à la
              nouvelle adresse — rien ne change avant que tu cliques le lien.
            </p>
            <button disabled={!nouvelEmail.includes("@")} className="btn-ghost text-sm"
              onClick={() => agir("/email", { email: nouvelEmail })}
              data-testid="espace-enregistrer-email">
              Confirmer ma nouvelle adresse
            </button>
          </div>

          {erreur && (
            <div className="text-sm rounded-2xl px-4 py-3"
              style={{ background: "#FEF2F2", color: "#B91C1C" }}>{erreur}</div>
          )}
          {message && (
            <p className="text-xs flex items-start gap-1" style={{ color: "#15803D" }}
              data-testid="espace-securite-ok">
              <Check size={12} className="shrink-0 mt-0.5" /> {message}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
